// #region Imports
import { styled, type Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import Guid from "_shared/guids/guid";
// #endregion

/**
 * A person who has left the team a fee schedule belongs to, and how.
 *
 * Structural rather than the work agreement's `VendorTeamDeparture`, so the shared tables do not
 * reach into `work/`; a `VendorTeamDeparture` is one. `label` is the short word drawn beside the name
 * ("Left", "Removed"), and `status` the whole wording a reader gets on hover and from a screen reader.
 */
export type FeeScheduleTeamMemberDeparture = {
  readonly userId: Guid;
  readonly label: string;
  readonly status: string;
};

/** The departures of a team, by user id, for a table to look each rate's person up in. */
export function indexDeparturesByUserId(
  departures: readonly FeeScheduleTeamMemberDeparture[] | undefined,
): ReadonlyMap<string, FeeScheduleTeamMemberDeparture> {
  return new Map((departures ?? []).map((departure) => [departure.userId.value, departure]));
}

/**
 * The departure a rate reads as, or undefined when it reads as staffed.
 *
 * A rate keeps the name of the person it was agreed for after they leave the team, and the work goes
 * on (#6096). It reads as departed until a change order restaffs it. On a proposal, pass `isOffTeam`:
 * a change order that puts the person back on the team has staffed the rate again, whatever the
 * agreement it replaces recorded.
 */
export function departureOfRate(
  category: { teamMember?: { userId: Guid } | null; teamMemberIsMasked: boolean },
  departures: ReadonlyMap<string, FeeScheduleTeamMemberDeparture>,
  isOffTeam = true,
): FeeScheduleTeamMemberDeparture | undefined {
  if (category.teamMemberIsMasked || !category.teamMember || !isOffTeam) return undefined;
  return departures.get(category.teamMember.userId.value);
}

/**
 * How the cells of a rate whose person departed read: struck through in the secondary tone (#6096).
 *
 * Thinner and lighter than a removed category's line, because the category is still agreed: only
 * who works it is gone. The Team Member cell draws its own line, so the label beside the name stays
 * readable, and the caller leaves that column out.
 *
 * A line on the cell is not drawn into an atomic inline box. A proposal's redline cell draws its value
 * inside a form control root holding an input root, both `inline-flex`, so in a browser its text read
 * as plain although the cell's computed style said it was struck through. Each root draws the line
 * itself. `inherit` would not do: it copies the parent's computed value, and a form control's is none.
 */
export function departedRateCellStyles(theme: Theme) {
  const struckThrough = {
    textDecoration: "line-through",
    textDecorationColor: theme.palette.text.disabled,
  };
  return {
    ...struckThrough,
    color: theme.palette.text.secondary,
    "& .MuiTypography-root": {
      color: "inherit",
    },
    "& .MuiFormControl-root, & .MuiInputBase-root": {
      ...struckThrough,
      color: "inherit",
    },
  };
}

// #region Styled Components
const DepartedMember = styled("span")(({ theme }) => ({
  alignItems: "baseline",
  color: theme.palette.text.disabled,
  display: "flex",
  gap: theme.spacing(0.75),
  minWidth: 0,
}));
/**
 * The name, struck through and grayed.
 *
 * The line is drawn on the name button as well as on the text around it. A button is an atomic
 * inline box, so a `line-through` on its parent is never drawn across it: in a browser the name read
 * as plain gray text, although the parent's computed style said it was struck through.
 */
const DepartedName = styled(Typography)(({ theme }) => {
  const struckThrough = {
    color: theme.palette.text.secondary,
    textDecoration: "line-through",
    textDecorationColor: theme.palette.text.disabled,
  };
  return {
    ...struckThrough,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "& .MuiLink-root, & .MuiLink-root:hover": struckThrough,
  };
});
// `inline-block` so the row's line-through, which every cell of a departed rate carries, is not drawn
// through the label too: a decoration does not reach into an atomic inline box.
const DepartureLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "inline-block",
  flexShrink: 0,
  fontStyle: "italic",
}));
// #endregion

type FeeScheduleDepartedTeamMemberProps = {
  /** The name as plain text, for the hover title. */
  name: string;
  /** What is drawn struck through: the name, or a control that opens their profile. */
  children: ReactNode;
  departure: FeeScheduleTeamMemberDeparture;
};

/** A rate's person who has left the team: their name struck through, then how they left. */
export default function FeeScheduleDepartedTeamMember(props: Readonly<FeeScheduleDepartedTeamMemberProps>) {
  const { name, children, departure } = props;
  return (
    <DepartedMember data-departed-team-member={departure.label} title={`${name}: ${departure.status}`}>
      <DepartedName variant="body2">{children}</DepartedName>
      <DepartureLabel variant="caption">{departure.label}</DepartureLabel>
    </DepartedMember>
  );
}
